import { useCanAccess, useGetList } from "ra-core";

import type { AgencyMember } from "../types";

const MEMBERSHIP_STALE_MS = 5 * 60 * 1000;

/**
 * The `agency_members.phone_digits` form of a WhatsApp id (the wa_id: E.164
 * digits, no "+"), or null when it cannot be one — the DB check is
 * `^[0-9]{7,15}$`.
 */
export function agencyMemberDigits(
  whatsappId: string | null | undefined,
): string | null {
  const digits = String(whatsappId ?? "").replace(/\D/g, "");
  return /^[0-9]{7,15}$/.test(digits) ? digits : null;
}

/**
 * True when this WhatsApp number belongs to a real-estate agency (a row in
 * `agency_members`, which also grants the implied Real Estate cohort). The
 * agency tables are admin-only: for everyone else this is false and no
 * request is made.
 */
export function useIsAgencyMember(
  whatsappId: string | null | undefined,
): boolean {
  const { canAccess } = useCanAccess({
    resource: "agency_members",
    action: "list",
  });
  const digits = agencyMemberDigits(whatsappId);
  const { data } = useGetList<AgencyMember>(
    "agency_members",
    {
      filter: { phone_digits: digits },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "created_at", order: "DESC" },
    },
    {
      enabled: canAccess === true && digits !== null,
      retry: false,
      staleTime: MEMBERSHIP_STALE_MS,
    },
  );
  return Boolean(
    digits && data?.some((member) => member.phone_digits === digits),
  );
}
